import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, RefreshCw, FileText } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function CategoriaForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isEditMode = !!id;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

    const [formData, setFormData] = useState({
        nombreCategoria: '',
        descripcion: '',
        tipoCategoriaId: 1 // Valor por defecto
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            cargarDatos();
        }
    }, [id]);

    const cargarDatos = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/parametrizacion/categorias/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFormData(data);
            } else {
                throw new Error('Error al cargar datos');
            }
        } catch (error) {
            toast.error('No se pudieron cargar los datos');
            navigate('/parametrizacion/categorias');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombreCategoria.trim()) {
            toast.error('El nombre de la categoría es requerido');
            return;
        }

        setLoading(true);
        try {
            const method = isEditMode ? 'PUT' : 'POST';
            const url = isEditMode ? `${API_URL}/parametrizacion/categorias/${id}` : `${API_URL}/parametrizacion/categorias`;
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                toast.success(isEditMode ? 'Categoría actualizada' : 'Categoría creada');
                setTimeout(() => navigate('/parametrizacion/categorias'), 1000);
            } else {
                throw new Error('Error al guardar');
            }
        } catch (error) {
            toast.error('Error al guardar la categoría');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-enterprise">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex items-center gap-3 text-white rounded-t-sm">
                <button
                    onClick={() => navigate('/parametrizacion/categorias')}
                    className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                    title="Volver"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-300" />
                    <h1 className="text-lg font-semibold tracking-tight">
                        {isEditMode ? 'Mantenimiento: Editar Categoría de Socio' : 'Mantenimiento: Nueva Categoría de Socio'}
                    </h1>
                </div>
            </div>

            {/* Form Area */}
            <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="md:col-span-3">
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                                Nombre de la Categoría <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                required
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-bold uppercase"
                                placeholder="Ej: AAA, AA, ESPECIAL"
                                value={formData.nombreCategoria}
                                onChange={(e) => setFormData({ ...formData, nombreCategoria: e.target.value.toUpperCase() })}
                            />
                        </div>

                        <div className="md:col-span-1">
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase text-center">
                                Tipo ID
                            </label>
                            <input
                                type="number"
                                required
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all text-center"
                                value={formData.tipoCategoriaId}
                                onChange={(e) => setFormData({ ...formData, tipoCategoriaId: parseInt(e.target.value) })}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                            Descripción / Observaciones
                        </label>
                        <textarea
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all min-h-[100px]"
                            placeholder="Detalle el criterio de esta categoría..."
                            value={formData.descripcion}
                            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                        />
                    </div>

                    <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate('/parametrizacion/categorias')}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors text-xs font-bold uppercase"
                        >
                            <X className="w-3.5 h-3.5 mr-1.5 inline-block" />
                            CANCELAR
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors text-xs font-bold uppercase disabled:opacity-70 shadow-md"
                        >
                            {loading ? (
                                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin inline-block" />
                            ) : (
                                <Save className="w-3.5 h-3.5 mr-1.5 inline-block" />
                            )}
                            {isEditMode ? 'ACTUALIZAR CATEGORÍA' : 'CREAR CATEGORÍA'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
